import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import { dailyCheckInsAPI } from '../utils/supabase'
import { getTodayDateString } from '../utils/dateUtils'

// Session-based cache: stores check-ins by date during session.
// Cache cleared on logout/page refresh to ensure fresh data.
// Each cache entry includes _cachedAt timestamp for TTL validation
export const CACHE_TTL = 5 * 60 * 1000 // 5 minutes - exported for use in components
const MAX_CACHE_SIZE = 20 // LRU cache limit to prevent unbounded growth

const initialState = {
  currentCheckIn: null,
  checkInCache: {},  // { "2025-01-15": { ...checkIn, _cachedAt: timestamp }, ... }
  loading: false,
  error: null,
  initialized: false
}

// Helper to enforce LRU cache size limit
const enforceCacheLimit = (cache) => {
  const cacheKeys = Object.keys(cache)
  if (cacheKeys.length >= MAX_CACHE_SIZE) {
    // Remove oldest entry by _cachedAt timestamp
    const oldestKey = cacheKeys.reduce((oldest, key) => {
      const oldestTime = cache[oldest]?._cachedAt || Infinity
      const currentTime = cache[key]?._cachedAt || Infinity
      return currentTime < oldestTime ? key : oldest
    }, cacheKeys[0])
    delete cache[oldestKey]
  }
}

// Async thunks with abort controller support
export const fetchDailyCheckIn = createAsyncThunk(
  'dailyCheckIns/fetch',
  async ({ userId, date }, { rejectWithValue, signal }) => {
    try {
      // Check if aborted before API call
      if (signal.aborted) {
        return rejectWithValue('Operation aborted')
      }

      const { data, error } = await dailyCheckInsAPI.getDailyCheckIn(userId, date)

      if (signal.aborted) {
        return rejectWithValue('Operation aborted')
      }

      if (error) {
        // Not found is expected for new days - return null instead of erroring
        if (error.code === 'PGRST116' || error.message?.includes('0 rows')) {
          return null
        }
        throw error
      }

      return data || null
    } catch (error) {
      if (signal.aborted) {
        return rejectWithValue('Operation aborted')
      }
      return rejectWithValue(error.message)
    }
  }
)

export const saveDailyCheckIn = createAsyncThunk(
  'dailyCheckIns/save',
  async ({ userId, checkInData }, { rejectWithValue, signal }) => {
    try {
      if (signal.aborted) {
        return rejectWithValue('Operation aborted')
      }

      const { data, error } = await dailyCheckInsAPI.saveDailyCheckIn(userId, checkInData)

      if (signal.aborted) {
        return rejectWithValue('Operation aborted')
      }

      if (error) throw error

      return data
    } catch (error) {
      if (signal.aborted) {
        return rejectWithValue('Operation aborted')
      }
      return rejectWithValue(error.message)
    }
  }
)

export const completeDailyCheckIn = createAsyncThunk(
  'dailyCheckIns/complete',
  async ({ userId, checkInData, taskUpdates }, { rejectWithValue, signal, dispatch }) => {
    try {
      if (signal.aborted) {
        return rejectWithValue('Operation aborted')
      }

      // 1. Save the check-in
      const { data: checkIn, error: checkInError } = await dailyCheckInsAPI.saveDailyCheckIn(userId, checkInData)

      if (signal.aborted) {
        return rejectWithValue('Operation aborted')
      }

      if (checkInError) throw checkInError

      // 2. Update task statuses
      // Import updateTask dynamically to avoid circular dependency
      const { updateTask } = await import('./tasksSlice')

      const updatePromises = taskUpdates.map(async (taskUpdate) => {
        if (signal.aborted) return null
        return dispatch(updateTask({
          taskId: taskUpdate.taskId,
          updates: taskUpdate.updates
        })).unwrap()
      })

      await Promise.all(updatePromises)

      if (signal.aborted) {
        return rejectWithValue('Operation aborted')
      }

      return checkIn
    } catch (error) {
      if (signal.aborted) {
        return rejectWithValue('Operation aborted')
      }
      return rejectWithValue(error.message)
    }
  }
)

const dailyCheckInsSlice = createSlice({
  name: 'dailyCheckIns',
  initialState,
  reducers: {
    // Optimistic update for focus goal changes
    updateFocusGoalOptimistic: (state, action) => {
      const { value, timezone } = action.payload
      if (state.currentCheckIn) {
        state.currentCheckIn.focus_goal = value
        // Update cache with timestamp
        if (state.currentCheckIn.check_in_date) {
          enforceCacheLimit(state.checkInCache)
          state.checkInCache[state.currentCheckIn.check_in_date] = {
            ...state.currentCheckIn,
            _cachedAt: Date.now()
          }
        }
      } else {
        // Create a temporary check-in if none exists
        const checkInDate = getTodayDateString(timezone)
        state.currentCheckIn = {
          focus_goal: value,
          big_3_task_ids: [],
          stretch_task_ids: [],
          check_in_date: checkInDate
        }
        // Cache the new check-in with timestamp
        enforceCacheLimit(state.checkInCache)
        state.checkInCache[checkInDate] = {
          ...state.currentCheckIn,
          _cachedAt: Date.now()
        }
      }
    },

    // Optimistic update for staged tasks
    updateStagedTasksOptimistic: (state, action) => {
      const { tasks, timezone } = action.payload
      if (state.currentCheckIn) {
        state.currentCheckIn.big_3_task_ids = tasks.slice(0, 3).map(t => t.id)
        state.currentCheckIn.stretch_task_ids = tasks.slice(3).map(t => t.id)
        // Update cache with timestamp
        if (state.currentCheckIn.check_in_date) {
          enforceCacheLimit(state.checkInCache)
          state.checkInCache[state.currentCheckIn.check_in_date] = {
            ...state.currentCheckIn,
            _cachedAt: Date.now()
          }
        }
      } else {
        const checkInDate = getTodayDateString(timezone)
        state.currentCheckIn = {
          focus_goal: '',
          big_3_task_ids: tasks.slice(0, 3).map(t => t.id),
          stretch_task_ids: tasks.slice(3).map(t => t.id),
          check_in_date: checkInDate
        }
        // Cache the new check-in with timestamp
        enforceCacheLimit(state.checkInCache)
        state.checkInCache[checkInDate] = {
          ...state.currentCheckIn,
          _cachedAt: Date.now()
        }
      }
    },

    // Optimistic update for evening reflection fields
    updateReflectionOptimistic: (state, action) => {
      const { timezone } = action.payload
      if (state.currentCheckIn) {
        // Use 'in' operator to distinguish between "not provided" and "explicitly set to null"
        if ('dayRating' in action.payload) state.currentCheckIn.day_rating = action.payload.dayRating
        if ('reflection' in action.payload) state.currentCheckIn.reflection = action.payload.reflection
        // Update cache with timestamp
        if (state.currentCheckIn.check_in_date) {
          enforceCacheLimit(state.checkInCache)
          state.checkInCache[state.currentCheckIn.check_in_date] = {
            ...state.currentCheckIn,
            _cachedAt: Date.now()
          }
        }
      } else {
        // Create temporary check-in if none exists
        const checkInDate = getTodayDateString(timezone)
        state.currentCheckIn = {
          focus_goal: '',
          big_3_task_ids: [],
          stretch_task_ids: [],
          day_rating: 'dayRating' in action.payload ? action.payload.dayRating : null,
          reflection: 'reflection' in action.payload ? action.payload.reflection : null,
          check_in_date: checkInDate
        }
        enforceCacheLimit(state.checkInCache)
        state.checkInCache[checkInDate] = {
          ...state.currentCheckIn,
          _cachedAt: Date.now()
        }
      }
    },

    // Set current check-in from cache (for date navigation)
    setCurrentCheckInFromCache: (state, action) => {
      state.currentCheckIn = action.payload
    },

    // Clear check-in (for logout or date change)
    clearDailyCheckIn: (state) => {
      state.currentCheckIn = null
      state.checkInCache = {}  // Clear entire cache
      state.loading = false
      state.error = null
      state.initialized = false
    },

    clearError: (state) => {
      state.error = null
    }
  },
  extraReducers: (builder) => {
    builder
      // Fetch daily check-in
      .addCase(fetchDailyCheckIn.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchDailyCheckIn.fulfilled, (state, action) => {
        state.loading = false
        state.currentCheckIn = action.payload
        state.initialized = true

        // Cache the check-in by date for session with timestamp
        if (action.payload?.check_in_date) {
          enforceCacheLimit(state.checkInCache)
          state.checkInCache[action.payload.check_in_date] = {
            ...action.payload,
            _cachedAt: Date.now()
          }
        }
      })
      .addCase(fetchDailyCheckIn.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
        state.initialized = true
      })

      // Save daily check-in
      .addCase(saveDailyCheckIn.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(saveDailyCheckIn.fulfilled, (state, action) => {
        state.loading = false
        state.currentCheckIn = action.payload

        // Update cache with saved check-in and timestamp
        if (action.payload?.check_in_date) {
          enforceCacheLimit(state.checkInCache)
          state.checkInCache[action.payload.check_in_date] = {
            ...action.payload,
            _cachedAt: Date.now()
          }
        }
      })
      .addCase(saveDailyCheckIn.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })

      // Complete daily check-in
      .addCase(completeDailyCheckIn.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(completeDailyCheckIn.fulfilled, (state, action) => {
        state.loading = false
        state.currentCheckIn = action.payload

        // Update cache with completed check-in and timestamp
        if (action.payload?.check_in_date) {
          enforceCacheLimit(state.checkInCache)
          state.checkInCache[action.payload.check_in_date] = {
            ...action.payload,
            _cachedAt: Date.now()
          }
        }
      })
      .addCase(completeDailyCheckIn.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })
  }
})

export const {
  updateFocusGoalOptimistic,
  updateStagedTasksOptimistic,
  updateReflectionOptimistic,
  setCurrentCheckInFromCache,
  clearDailyCheckIn,
  clearError
} = dailyCheckInsSlice.actions

// Selectors
export const selectCurrentCheckIn = (state) => state.dailyCheckIns.currentCheckIn
export const selectDailyCheckInsLoading = (state) => state.dailyCheckIns.loading
export const selectDailyCheckInsError = (state) => state.dailyCheckIns.error
export const selectDailyCheckInsInitialized = (state) => state.dailyCheckIns.initialized
export const selectCheckInByDate = (state, date) => state.dailyCheckIns.checkInCache[date] || null

export default dailyCheckInsSlice.reducer
